// ---------------------------------------------------------------------------
// Sync status (PLAN 8).
//
// This screen exists because of the operational gap in PLAN 5.7: records only
// move while the app is open, so a volunteer who finishes a shift and closes
// the app leaves that data on their phone. The pending count is the thing that
// stops that happening, so it is stated plainly rather than tucked away.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatAddress } from '@yucanvas/shared';
import { db } from '../offline/db';
import { onSyncState, syncNow, type SyncState } from '../offline/sync';
import { isBasemapCached } from '../offline/basemap';
import { readStorage, requestPersistence, type StorageReport } from '../offline/storage';
import { formatBytes } from '../lib/bytes';
import { relativeTime } from '../lib/time';

export function SyncScreen() {
  const [state, setState] = useState<SyncState | null>(null);
  useEffect(() => onSyncState(setState), []);

  const [storage, setStorage] = useState<StorageReport | null>(null);
  const [basemap, setBasemap] = useState<boolean | null>(null);
  const [persisting, setPersisting] = useState(false);

  useEffect(() => {
    void readStorage().then(setStorage);
    void isBasemapCached().then(setBasemap);
  }, []);

  async function keepData(): Promise<void> {
    setPersisting(true);
    try {
      await requestPersistence();
      setStorage(await readStorage());
    } finally {
      setPersisting(false);
    }
  }

  const failing = useLiveQuery(
    () => db.visits.where('synced').equals(0).filter((v) => v.attempts > 0).toArray(),
    [],
  );

  // Anything the server has refused. A row set aside so the rest of the queue
  // can drain must still be visible, or it is just silent data loss.
  const stuck = useLiveQuery(async () => {
    const [homes, residents] = await Promise.all([
      db.households.filter((h) => (h.attempts ?? 0) > 0 && h.last_error != null).toArray(),
      db.people.filter((p) => (p.attempts ?? 0) > 0 && p.last_error != null).toArray(),
    ]);
    return [
      ...homes.map((h) => ({ what: `Home: ${h.street_address}`, why: h.last_error! })),
      ...residents.map((p) => ({
        what: `Resident: ${p.first_name} ${p.last_name}`.trim(),
        why: p.last_error!,
      })),
    ];
  }, []);

  const withAddress = useLiveQuery(async () => {
    const rows = await db.visits.where('synced').equals(0).toArray();
    return Promise.all(
      rows.map(async (v) => ({
        visit: v,
        household: await db.households.get(v.household_id),
      })),
    );
  }, []);

  const pending = state?.pending ?? 0;

  return (
    <main className="page">
      <header className="page__head">
        <div>
          <h1>Sync</h1>
          <p className="page__sub">{state?.online ? 'Online' : 'No connection'}</p>
        </div>
        <Link className="btn btn--link" to="/turfs">My turf</Link>
      </header>

      <section className="card">
        <p className={`sync-count${pending === 0 ? ' sync-count--clear' : ''}`}>
          {pending === 0 ? 'Everything is uploaded' : `${pending} waiting to upload`}
        </p>
        <p className="turf__meta">Last successful sync: {relativeTime(state?.lastSyncAt ?? null)}</p>
        {state?.lastError ? (
          <p className="banner banner--error">{state.lastError}</p>
        ) : null}
        <button
          className="btn btn--primary btn--huge"
          disabled={state?.running}
          onClick={() => void syncNow({ retryRejected: true })}
        >
          {state?.running ? 'Syncing…' : 'Sync now'}
        </button>
        {pending > 0 ? (
          <p className="turf__meta">
            Keep this app open until the count reaches zero. Visits only upload
            while it is running.
          </p>
        ) : null}
      </section>

      {pending > 0 ? (
        <>
          <h2 className="organize__section">Waiting</h2>
          <ul className="list">
            {withAddress?.map(({ visit, household }) => (
              <li key={visit.id}>
                <div className="row">
                  <span className="row__addr">
                    {household ? formatAddress(household) : visit.household_id}
                  </span>
                  <span className="row__state">
                    {visit.attempts > 0 ? `${visit.attempts} attempts` : 'queued'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h2 className="organize__section">Storage</h2>
      <section className="card">
        {storage?.supported ? (
          <p className="turf__meta">
            Using {formatBytes(storage.usage ?? 0)}
            {storage.quota !== null ? ` of ${formatBytes(storage.quota)} available` : ''}.
          </p>
        ) : (
          <p className="turf__meta">This browser does not report storage use.</p>
        )}

        <p className="turf__meta">
          Offline map:{' '}
          {basemap === null ? 'checking…' : basemap ? 'downloaded' : 'not downloaded'}
        </p>

        {storage?.persisted ? (
          <p className="turf__meta">
            Your device has been asked to keep this data. It will not be cleared
            to free up space.
          </p>
        ) : storage?.canRequestPersistence ? (
          <>
            <p className="turf__meta">
              This data can be cleared automatically if the phone runs low on
              space.
            </p>
            <button
              className="btn btn--secondary"
              disabled={persisting}
              onClick={() => void keepData()}
            >
              {persisting ? 'Asking…' : 'Keep my data'}
            </button>
          </>
        ) : (
          // Safari grants durability to home-screen installs by itself and has
          // no API to ask, so silence here is not bad news (PLAN 6, 13.7).
          <p className="turf__meta">
            Install the app to your home screen to keep this data safe.
          </p>
        )}
      </section>

      {stuck && stuck.length > 0 ? (
        <>
          <h2 className="organize__section">Needs attention</h2>
          <p className="muted small">
            The server refused these. Everything else has still uploaded.
          </p>
          <ul className="list">
            {stuck.map((row, i) => (
              <li key={i}>
                <div className="row">
                  <span className="row__addr">{row.what}</span>
                </div>
                <p className="note__by">{row.why}</p>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {failing && failing.length > 0 ? (
        <>
          <h2 className="organize__section">Errors</h2>
          <ul className="setup">
            {[...new Set(failing.map((v) => v.last_error))].map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </>
      ) : null}
    </main>
  );
}
